import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader, RadioGroup,
  Typography
} from '@one-thd/sui-atomic-components';
import { AddressShape } from '../shapes/Address';
import { ADDRESS_PATH, POSTAL_MAIL_UNSUBSCRIBE_SUCCESS } from '../constants';
import { standardizeAddress, unsubscribeDirectMail } from '../apis/address';
import { BannerContext, BannerProvider } from '../contexts/BannerContext';
import { addAnalyticEvent } from '../analytics/common';
import {
  verifyMailingEvent,
  removeOverlay,
  unsubscribeFromPostalMail,
  verifyOverlay,
  confirmVerifiedMailingEvent,
  confirmUnverifiedMailingEvent,
  verifyOverlayError,
  confirmVerifiedMailingError, confirmUnverifiedMailingError
} from '../analytics/address';
import ErrorBoundary from '../contexts/ErrorBoundary';
import { AddressBox, DisabledAddressBox } from './common/AddressBox';

export const unsubPostalMailButtonLabel = 'unsub-postal-mail-button-label';
export const standardizeAddressButton = 'standardize-address-button-label';
export const confirmButtonLabel = 'confirm-button-label';
export const closeButtonLabel = 'confirm-button-label';

const Actions = {
  RESET: 'RESET',
  CONFIRM_ADDRESS: 'CONFIRM_ADDRESS',
  NO_ADDRESS: 'NO_ADDRESS',
  SELECT_ADDRESS: 'SELECT_ADDRESS',
  NO_STANDARD_ADDRESS: 'NO_STANDARD_ADDRESS',
  NO_NAME_SELECT_ADDRESS: 'NO_NAME_SELECT_ADDRESS',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
};

function initialState() {
  return {
    showNoAddressesModal: false,
    showConfirmAddressesModal: false,
    showSelectAddressModal: false,
    showConfirmUnVerifiedAddress: false,
    showNoNameAddressesModal: false,
    selectedAddress: null,
    standardizedAddress: null,
  };
}

const verified = 'verified';

function reducer(state, action) {
  switch (action.type) {
  case Actions.SELECT_ADDRESS:
    addAnalyticEvent(removeOverlay);
    return {
      ...state,
      showSelectAddressModal: true,
    };
  case Actions.NO_NAME_SELECT_ADDRESS:
    addAnalyticEvent(removeOverlay);
    return {
      ...state,
      showNoNameAddressesModal: true,
      showSelectAddressModal: false,
      selectedAddress: action.payload.selectedAddress,
    };
  case Actions.NO_ADDRESS:
    return {
      ...state,
      showNoAddressesModal: true,
    };
  case Actions.CONFIRM_ADDRESS:
    addAnalyticEvent(verifyOverlay);
    return {
      ...state,
      showConfirmAddressesModal: true,
      showSelectAddressModal: false,
      selectedAddress: action.payload.selectedAddress,
      standardizedAddress: action.payload?.standardizedAddress,
    };
  case Actions.NO_STANDARD_ADDRESS:
    addAnalyticEvent(verifyOverlay);
    return {
      ...state,
      showConfirmUnVerifiedAddress: true,
      showSelectAddressModal: false,
      selectedAddress: action.payload.selectedAddress,
    };
  case Actions.ERROR:
  case Actions.SUCCESS:
  case Actions.RESET:
  default:
    return initialState();
  }
}

export const Preferences = ({ addresses }) => {

  const {
    openSuccessAlert,
    openErrorAlert,
    closeSuccessAlert,
  } = React.useContext(BannerContext);

  const [state, dispatch] = React.useReducer(reducer, undefined, initialState);

  const handleClose = () => {
    dispatch({ type: Actions.RESET });
  };

  const handleUnsubscribeClick = () => {
    addAnalyticEvent(unsubscribeFromPostalMail);
    if (addresses.length === 0) {
      dispatch({ type: Actions.NO_ADDRESS });
    } else {
      dispatch({ type: Actions.SELECT_ADDRESS });
    }
  };

  function displayAddress(addr, title) {
    return (
      <div>
        {title && (
          <Typography variant="body-base" weight="bold">
            {title}
          </Typography>
        )}
        <br />
        {AddressBox(addr)}
      </div>
    );
  }

  const NoNameAddressModal = () => {

    return state.showNoNameAddressesModal && (
      <Modal onClose={handleClose} open={state.showNoNameAddressesModal}>
        <ModalHeader onClose={handleClose}>
          Remove From Mailing List
        </ModalHeader>
        <ModalBody>

          <Typography variant="body-base">
            {`You must have a first and last name in your address in order to be removed from the
            Mailing List. To edit your information, please visit your `}
            <Link href={ADDRESS_PATH}>Addresses</Link>
            {' before continuing.'}
            <br />
          </Typography>

          <RadioGroup orientation="horizontal" value={state.selectedAddress.addressIdentifier}>
            {DisabledAddressBox(state.selectedAddress)}
          </RadioGroup>
          <Typography variant="body-xs" color="danger">
            You must have a first and last name in your address in order to be removed from the Mailing List.
          </Typography>

        </ModalBody>
        <ModalFooter position="end">
          <Button aria-label={closeButtonLabel} onClick={handleClose}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  const SelectAddressModal = () => {
    const primaryAddress = addresses.find((addr) => addr.isDefault);
    const [selected, setValue] = React.useState(primaryAddress?.addressIdentifier);

    const handleChange = (event) => {
      setValue(event.target.value);
    };
    useEffect(() => {
      setValue(primaryAddress?.addressIdentifier);
    }, [state.showSelectAddressModal]);

    const handleContinue = () => {

      const address = addresses.find((addr) => addr.addressIdentifier === parseInt(selected, 10));

      function successCallback(res) {
        addAnalyticEvent(verifyMailingEvent);
        let standardizedAddress = {
          ...res.data,
          firstName: address.firstName,
          lastName: address.lastName,
          addressIdentifier: verified,
          standardized: true,
        };

        const hasAddress = standardizedAddress && standardizedAddress.addressLine1;

        if (hasAddress) {
          dispatch({ type: Actions.CONFIRM_ADDRESS,
            payload: {
              selectedAddress: address,
              standardizedAddress,
            } });
        } else {
          dispatch({ type: Actions.NO_STANDARD_ADDRESS,
            payload: {
              selectedAddress: address,
            } });
        }
      }

      function errorCallback() {
        addAnalyticEvent(verifyOverlayError);
        dispatch({ type: Actions.NO_STANDARD_ADDRESS,
          payload: {
            selectedAddress: address,
          } });
      }

      if (address && address.firstName && address.lastName) {
        standardizeAddress(address, successCallback, errorCallback);
      } else {
        addAnalyticEvent(verifyOverlayError);
        dispatch({ type: Actions.NO_NAME_SELECT_ADDRESS,
          payload: {
            selectedAddress: address,
          } });
      }

    };

    return state.showSelectAddressModal && (
      <Modal onClose={handleClose} open={state.showSelectAddressModal}>
        <ModalHeader onClose={handleClose}>
          Remove From Mailing List
        </ModalHeader>
        <ModalBody>

          <Typography variant="body-base">
            {`To be removed from The Home Depot mailing list, please select the correct mailing information
            below. To edit your information, please visit your `}
            <Link href={ADDRESS_PATH}>Addresses</Link>
            {' before continuing.'}
            <br />
          </Typography>

          <br />

          <RadioGroup orientation="horizontal" value={selected} onChange={handleChange}>
            {addresses.map(AddressBox)}
          </RadioGroup>

        </ModalBody>
        <ModalFooter position="end">
          <Button disabled={!selected} aria-label={standardizeAddressButton} onClick={handleContinue}>
            Continue
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  function ConfirmAddressesModal() {
    const [selected, setValue] = React.useState(verified);

    useEffect(() => {
      setValue(verified);
    }, [state.showConfirmAddressesModal]);

    const handleChange = (event) => {
      setValue(event.target.value);
    };

    const handleConfirm = () => {
      closeSuccessAlert();
      let addr = (selected === verified) ? state.standardizedAddress : state.selectedAddress;
      unsubscribeDirectMail(addr, () => {
        addAnalyticEvent(confirmVerifiedMailingEvent);
        openSuccessAlert(POSTAL_MAIL_UNSUBSCRIBE_SUCCESS, addr);
        dispatch({ type: Actions.SUCCESS });
      }, () => {
        addAnalyticEvent(confirmVerifiedMailingError);
        openErrorAlert();
        dispatch({ type: Actions.ERROR });
      });
    };

    return state.showConfirmAddressesModal && (
      <Modal onClose={handleClose} open={state.showConfirmAddressesModal}>
        <ModalHeader onClose={handleClose}>
          Confirm Your Mailing Address
        </ModalHeader>
        <ModalBody>

          <Typography variant="body-base">
            {`The address entered matched the verified address provided by United Parcel Service
              (UPS). The Home Depot recommends using the Verified Address option. We may not be
              able to remove the Unverified Address you entered because it may be invalid.`}
          </Typography>

          <br />
          <Typography variant="body-base">
            {'Please confirm the correct address or update your information in your '}
            <Link href={ADDRESS_PATH}>Addresses
            </Link>.
          </Typography>

          <br />
          <br />

          <RadioGroup orientation="horizontal" value={selected} onChange={handleChange}>
            {displayAddress(state.standardizedAddress, 'Verified Address')}
            <br />
            {displayAddress(state.selectedAddress, 'Unverified Address')}
          </RadioGroup>

        </ModalBody>
        <ModalFooter position="end">
          <Button aria-label={confirmButtonLabel} onClick={handleConfirm}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  function ConfirmUnverifiedAddressModal() {

    const handleConfirm = () => {
      closeSuccessAlert();
      let addr = state.selectedAddress;
      unsubscribeDirectMail(addr, () => {
        addAnalyticEvent(confirmUnverifiedMailingEvent);
        openSuccessAlert(POSTAL_MAIL_UNSUBSCRIBE_SUCCESS, addr);
        dispatch({ type: Actions.SUCCESS });
      }, () => {
        addAnalyticEvent(confirmUnverifiedMailingError);
        openErrorAlert();
        dispatch({ type: Actions.ERROR });
      });
    };

    return state.showConfirmUnVerifiedAddress && (
      <Modal onClose={handleClose} open={state.showConfirmUnVerifiedAddress}>
        <ModalHeader onClose={handleClose}>
          Confirm Your Mailing Address
        </ModalHeader>
        <ModalBody>

          <Typography variant="body-base">
            {`The address entered did not match a verified address provided by United Parcel
            Service (UPS). If you have received information from us in the past, enter your address
            exactly as it appears on the correspondence. Any errors may prevent removal from our mailing list.`}
          </Typography>

          <br />
          <Typography variant="body-base">
            {'Please confirm the correct address or update your information in your '}
            <Link href={ADDRESS_PATH}>Addresses
            </Link>.
          </Typography>

          <br />
          <br />

          <RadioGroup orientation="horizontal" value={state.selectedAddress.addressIdentifier}>
            {displayAddress(state.selectedAddress, 'Entered Mailing Address')}
          </RadioGroup>

        </ModalBody>
        <ModalFooter position="end">
          <Button aria-label={confirmButtonLabel} onClick={handleConfirm}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  function NoAddressesModal() {
    return state.showNoAddressesModal && (
      <Modal onClose={handleClose} open={state.showNoAddressesModal}>
        <ModalHeader onClose={handleClose}>
          Remove From Mailing List
        </ModalHeader>
        <ModalBody>

          <Typography variant="body-base">
            {`To be removed from The Home Depot mailing list, there must be a saved
            address on your account. Want to add one now? Go to your `}
            <Link href={ADDRESS_PATH}>
              Addresses
            </Link>.
            <br />
          </Typography>

          <br />

        </ModalBody>
        <ModalFooter position="end">
          <Button onClick={handleClose}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <div className="sui-grid sui-gap-4">
      <Typography variant="h2" weight="display">
        POSTAL MAIL
      </Typography>
      <Typography variant="body-base">
        {`The Home Depot occasionally will send information about products, services, special offers
      and other promotions by mail. If you do not want to receive promotional mail from The Home Depot,
      please click on the ‘Unsubscribe’ button below to be removed from the mailing list.`}
      </Typography>
      <div className="sui-grid sui-grid-cols-8">
        <div className="hidden sm:sui-col-span-4 md:sui-col-span-5" />
        <div className="sui-col-span-8 sm:sui-col-span-4 md:sui-col-span-3">
          <Button
            variant="secondary"
            aria-label={unsubPostalMailButtonLabel}
            fullWidth
            onClick={handleUnsubscribeClick}
          >
            Opt Out From Postal Mail
          </Button>
        </div>
      </div>
      {NoAddressesModal()}
      {SelectAddressModal()}
      {ConfirmAddressesModal()}
      {ConfirmUnverifiedAddressModal()}
      {NoNameAddressModal()}
    </div>
  );
};

function MailingPreferences({ addresses }) {
  return (
    <ErrorBoundary>
      <BannerProvider>
        <Preferences addresses={addresses} />
      </BannerProvider>
    </ErrorBoundary>
  );
}

export default MailingPreferences;

const propTypes = {
  addresses: PropTypes.arrayOf(AddressShape).isRequired,
};

MailingPreferences.propTypes = propTypes;
Preferences.propTypes = propTypes;
